import React, { useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Typography } from '@material-ui/core'

const useStyles = makeStyles((theme) => ({
  calendlyContainer: {
    width: '100%',
    maxWidth: '45rem',
    margin: '0 auto',
    background: 'rgba(10, 10, 10, 0.98)',
    border: '2px solid #ff00ff',
    borderRadius: '12px',
    padding: '0',
    boxShadow: '0 0 30px rgba(255, 0, 255, 0.4), inset 0 0 60px rgba(0, 255, 255, 0.05)',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      minHeight: '59rem',
      padding: '0',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '0',
      borderRadius: '8px',
      border: '1px solid #ff00ff',
      margin: '0',
    },
    [theme.breakpoints.down('xs')]: {
      padding: '0',
      margin: '0',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(0deg, transparent 0%, rgba(255, 0, 255, 0.03) 50%, transparent 100%)',
      animation: '$scanLine 4s linear infinite',
      pointerEvents: 'none',
      zIndex: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '-2px',
      left: '-2px',
      right: '-2px',
      bottom: '-2px',
      background: 'linear-gradient(45deg, #ff00ff, #00ffff, #ff0080, #ff00ff)',
      borderRadius: '12px',
      zIndex: -1,
      opacity: 0.3,
      filter: 'blur(8px)',
      animation: '$borderGlow 3s ease-in-out infinite',
    },
  },
  '@keyframes scanLine': {
    '0%': {
      transform: 'translateY(-100%)',
    },
    '100%': {
      transform: 'translateY(100%)',
    },
  },
  '@keyframes borderGlow': {
    '0%, 100%': {
      opacity: 0.3,
    },
    '50%': {
      opacity: 0.6,
    },
  },
  '@keyframes pulse': {
    '0%, 100%': {
      textShadow: '0 0 10px rgba(0, 255, 255, 0.8), 0 0 20px rgba(0, 255, 255, 0.4)',
    },
    '50%': {
      textShadow: '0 0 20px rgba(0, 255, 255, 1), 0 0 30px rgba(0, 255, 255, 0.6), 0 0 40px rgba(255, 0, 255, 0.4)',
    },
  },
  '@keyframes glitch': {
    '0%, 100%': {
      transform: 'translate(0)',
    },
    '20%': {
      transform: 'translate(-2px, 2px)',
    },
    '40%': {
      transform: 'translate(-2px, -2px)',
    },
    '60%': {
      transform: 'translate(2px, 2px)',
    },
    '80%': {
      transform: 'translate(2px, -2px)',
    },
  },
  calendlyTitle: {
    fontFamily: '"Share Tech Mono", monospace',
    color: '#00ffff',
    fontSize: '1.8rem',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '2rem',
    textShadow: '0 0 10px rgba(0, 255, 255, 0.8), 0 0 20px rgba(0, 255, 255, 0.4)',
    animation: '$pulse 2s ease-in-out infinite',
    position: 'relative',
    zIndex: 2,
    letterSpacing: '2px',
    '&::before': {
      content: '">>>"',
      marginRight: '10px',
      color: '#ff00ff',
    },
    '&::after': {
      content: '"<<<"',
      marginLeft: '10px',
      color: '#ff00ff',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem',
      marginBottom: '0',
      letterSpacing: '1px',
      '&::before': {
        content: '">>"',
        marginRight: '5px',
      },
      '&::after': {
        content: '"<<"',
        marginLeft: '5px',
      },
    },
  },
  calendlyWidget: {
    minHeight: '550px',
    width: '100%',
    border: 'none',
    borderRadius: '8px',
    background: '#ffffff',
    position: 'relative',
    zIndex: 2,
  },
  cornerDecoration: {
    position: 'absolute',
    width: '1.8rem',
    height: '1.8rem',
    border: '2px solid #00ffff',
    zIndex: 3,
    pointerEvents: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  topLeft: {
    top: '10px',
    left: '10px',
    borderRight: 'none',
    borderBottom: 'none',
    boxShadow: '-2px -2px 10px rgba(0, 255, 255, 0.3)',
  },
  topRight: {
    top: '10px',
    right: '10px',
    borderLeft: 'none',
    borderBottom: 'none',
    boxShadow: '2px -2px 10px rgba(0, 255, 255, 0.3)',
  },
  bottomLeft: {
    bottom: '10px',
    left: '10px',
    borderRight: 'none',
    borderTop: 'none',
    boxShadow: '-2px 2px 10px rgba(255, 0, 255, 0.3)',
  },
  bottomRight: {
    bottom: '10px',
    right: '10px',
    borderLeft: 'none',
    borderTop: 'none',
    boxShadow: '2px 2px 10px rgba(255, 0, 255, 0.3)',
  },
  statusBar: {
    position: 'absolute',
    top: '10px',
    right: '60px',
    fontFamily: '"Share Tech Mono", monospace',
    fontSize: '0.7rem',
    color: '#00ff00',
    textShadow: '0 0 5px rgba(0, 255, 0, 0.8)',
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    animation: '$glitch 5s infinite',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#00ff00',
    boxShadow: '0 0 10px rgba(0, 255, 0, 0.8)',
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  loadingText: {
    fontFamily: '"Share Tech Mono", monospace',
    color: '#ff00ff',
    fontSize: '1rem',
    textAlign: 'center',
    padding: '50px',
    textShadow: '0 0 8px rgba(255, 0, 255, 0.6)',
  },
  browserWarning: {
    marginTop: '0',
    padding: '20px',
    background: 'rgba(255, 0, 255, 0.05)',
    border: '1px solid rgba(255, 0, 255, 0.3)',
    borderRadius: '8px',
    fontFamily: '"Share Tech Mono", monospace',
    fontSize: '0.85rem',
    color: '#00ffff',
    textAlign: 'center',
    textShadow: '0 0 5px rgba(0, 255, 255, 0.5)',
    position: 'relative',
    zIndex: 2,
    animation: '$pulse 3s ease-in-out infinite',
    '& .warning-icon': {
      color: '#ff0080',
      fontSize: '1.2rem',
      marginBottom: '10px',
      display: 'block',
      textShadow: '0 0 8px rgba(255, 0, 128, 0.8)',
    },
    '& .browser-list': {
      marginTop: '10px',
      color: '#ff00ff',
      fontSize: '0.9rem',
      fontWeight: 'bold',
    },
  }
}))

const CalendlyScheduler = ({ calendlyUrl = '' }) => {
  const classes = useStyles()
  const [loadError, setLoadError] = React.useState(false)
  const [isChrome, setIsChrome] = React.useState(false)

  useEffect(() => {
    // Detect if browser is Chrome
    const userAgent = navigator.userAgent.toLowerCase()
    const isChromeBrowser = /chrome/.test(userAgent) && !/edg/.test(userAgent) && !/opr/.test(userAgent)
    setIsChrome(isChromeBrowser)
  }, [])

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script')
    script.src = 'https://assets.calendly.com/assets/external/widget.js'
    script.async = true
    document.head.appendChild(script)

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]')
      if (existingScript) {
        document.head.removeChild(existingScript)
      }
    }
  }, [])

  if (!calendlyUrl) {
    return (
      <Box className={classes.calendlyContainer}>
        <Typography className={classes.calendlyTitle}>
          [CALENDLY_SCHEDULER]
        </Typography>
        <Box className={classes.loadingText}>
          ⚠ CALENDLY_URL_NOT_CONFIGURED<br/>
          Please provide your Calendly URL in the environment configuration.
        </Box>
      </Box>
    )
  }

  // Add cyberpunk URL parameters to Calendly
  // Using the color scheme from your example: primary_color=00ecfc (cyan), background_color=f167d4 (magenta/pink)
  const cyberpunkCalendlyUrl = `${calendlyUrl}?hide_landing_page_details=1&hide_gdpr_banner=1&primary_color=00ecfc&text_color=ffffff&background_color=1a0a1a`

  return (
    <Box className={classes.calendlyContainer}>
      {/* Corner Decorations */}
      <Box className={`${classes.cornerDecoration} ${classes.topLeft}`}/>
      <Box className={`${classes.cornerDecoration} ${classes.topRight}`}/>
      <Box className={`${classes.cornerDecoration} ${classes.bottomLeft}`}/>
      <Box className={`${classes.cornerDecoration} ${classes.bottomRight}`}/>

      {/* Status Bar */}
      {/*<Box className={classes.statusBar}>*/}
      {/*  <Box className={classes.statusDot}/>*/}
      {/*  <span>SYSTEM_ONLINE</span>*/}
      {/*</Box>*/}

      <Typography className={classes.calendlyTitle}>
        SCHEDULE_APPOINTMENT
      </Typography>
      <div
        className="calendly-inline-widget cyberpunk-calendly"
        data-url={cyberpunkCalendlyUrl}
        style={{
          // padding: '0px',
          // margin: '0px',
          width: '100%',
          height: '59rem',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid rgba(0, 255, 255, 0.2)',
          boxShadow: '0 0 20px rgba(0, 255, 255, 0.1)'
        }}
      />

      {/* Browser compatibility warning - only show for Chrome */}
      {isChrome && (
        <Box className={classes.browserWarning}>
          <span className="warning-icon">⚠ SYSTEM_ADVISORY</span>
          <div>
            {'>> CHROME_BROWSER_COMPATIBILITY_ISSUE_DETECTED'}
          </div>
          <div style={{ marginTop: '8px', fontSize: '0.8rem' }}>
            IF SCHEDULING_PROTOCOL_FAILS
          </div>
          <div className="browser-list">
            RECOMMEND: FIREFOX | SAFARI | EDGE
          </div>
        </Box>
      )}
    </Box>
  )
}

export default CalendlyScheduler
